/**
 * Zod schemas for /api/search/home-listings endpoint
 *
 * Request validation and response schemas for home page grid listings
 * Excludes Pets (16), Miscellaneous (27), and Jobs (31) categories
 */

import { z } from 'zod';
import { parseListingSort } from '../../../utils/listing-sort';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

/**
 * Request query parameters for home listings
 */
export const HomeListingsRequestSchema = z.object({
  q: z.string().optional(),
  page: z.string().optional().default('1'),
  limit: z.string().optional().default('32'),
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  itemCondition: z.string().optional(),
  shippable: z.string().optional(),
  status: z.string().optional().default('1'),
  // A shopper-picked order (utils/listing-sort.ts); anything else reads as none.
  sortBy: z.string().optional().transform((val) => parseListingSort(val)),
});

export type HomeListingsRequest = z.infer<typeof HomeListingsRequestSchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

/**
 * Photo object in listing response
 */
export const ListingPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
  '320x320Url': z.string(),
});

export type ListingPhoto = z.infer<typeof ListingPhotoSchema>;

/**
 * User object in listing response
 */
// User photo object - matches format from getUserPhotoUrls
export const UserPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
}).nullable();

export const ListingUserSchema = z.object({
  id: z.union([z.string(), z.number()]),
  firstName: z.string(),
  lastName: z.string(),
  username: z.string(),
  trustLevel: z.number(),
  photos: UserPhotoSchema,
}).nullable();

export type ListingUser = z.infer<typeof ListingUserSchema>;

/**
 * Individual listing in the response
 */
export const HomeListingSchema = z.object({
  id: z.string(),
  title: z.string(),
  text: z.string(),
  price: z.number(),
  cost: z.number(),
  original_price: z.number().nullish(),
  slashedPrice: z.string().nullish(),
  percentOff: z.number().nullish(),
  categoryId: z.number(),
  categoryGroupId: z.number(),
  categoryName: z.string().optional(),
  itemCondition: z.number().nullish(),
  status: z.number(),
  shippable: z.union([z.number(), z.boolean()]),
  locationText: z.string().nullish(),
  createdDate: z.union([z.string(), z.date()]).nullish(),
  featuredDate: z.union([z.string(), z.date()]).nullish(),
  tags: z.array(z.string()),
  dataSourceId: z.number().nullish(),
  remote_url: z.string().nullish(),
  hasPhoto: z.number(),
  photo: z.string().nullish(),
  photos: z.array(ListingPhotoSchema),
  user: ListingUserSchema,
  distance: z.number(),
  /** This seller's live listings of the same item, this one included (utils/listing-copies.ts). */
  copies: z.number().int().positive().optional(),
});

export type HomeListing = z.infer<typeof HomeListingSchema>;

/**
 * Success response for home listings
 */
export const HomeListingsSuccessResponseSchema = z.object({
  success: z.literal(true),
  listings: z.array(HomeListingSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
});

export type HomeListingsSuccessResponse = z.infer<typeof HomeListingsSuccessResponseSchema>;

/**
 * Error response for home listings
 */
export const HomeListingsErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type HomeListingsErrorResponse = z.infer<typeof HomeListingsErrorResponseSchema>;

/**
 * Combined response schema (discriminated union)
 */
export const HomeListingsResponseSchema = z.discriminatedUnion('success', [
  HomeListingsSuccessResponseSchema,
  HomeListingsErrorResponseSchema,
]);

export type HomeListingsResponse = z.infer<typeof HomeListingsResponseSchema>;
